import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Building2, Loader2, MousePointerClick, Eye, LogOut, Save, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { supplierLabels } from "@/data/supplierCategories";

function RequestLink() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/partner/request-link", { email }); setSent(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send link"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="partner-login">
      <div className="bg-card border border-border rounded-2xl p-8 shadow-sm text-center">
        <Building2 className="w-10 h-10 mx-auto mb-3 text-primary" />
        <h1 className="font-display text-2xl font-bold mb-1">Partner portal</h1>
        <p className="text-sm text-muted-foreground mb-6">Enter the contact email on your advertising package and we'll email you a secure sign-in link.</p>
        {sent ? (
          <p className="text-sm text-accent font-medium" data-testid="partner-link-sent">Check your inbox — if that email is on a partner listing, your link is on its way.</p>
        ) : (
          <form onSubmit={submit} className="flex gap-2" data-testid="partner-login-form">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.co.uk" data-testid="partner-email-input" />
            <Button type="submit" disabled={busy} data-testid="partner-request-link">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send link"}</Button>
          </form>
        )}
        <p className="text-xs text-muted-foreground mt-6">Not a partner yet? <Link to="/advertise" className="text-primary hover:underline">See advertising packages</Link>.</p>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className="font-display text-2xl font-extrabold" data-testid={testId}>{value}</p>
    </div>
  );
}

export default function Partner() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    api.get("/partner/me", { headers })
      .then((r) => { setData(r.data); setForm({ company: r.data.sponsor.company, description: r.data.sponsor.description, url: r.data.sponsor.url, keywords: (r.data.sponsor.keywords || []).join(", ") }); setStatus("ok"); if (token) setParams({}, { replace: true }); })
      .catch((err) => { if (err.response?.status !== 401) logError("Partner load failed", err); setStatus("anon"); });
  }, [params, setParams]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data: sp } = await api.patch("/partner/me", { ...form, keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean) });
      setData((d) => ({ ...d, sponsor: sp })); toast.success("Listing updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const logout = async () => { await api.post("/partner/logout"); setStatus("anon"); setData(null); };

  if (status === "loading") return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (status === "anon") return <RequestLink />;

  const { sponsor, tier, totals, last_30_days } = data;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="partner-portal">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline text-accent">Partner portal</span>
          <h1 className="font-display text-3xl font-bold" data-testid="partner-company">{sponsor.company}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {tier ? <Badge variant="secondary" className="mr-2 capitalize">{tier.name} package</Badge> : null}
            <span>{supplierLabels(sponsor)}</span> · {sponsor.active ? "Live" : "Not live"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="partner-renew"><Link to="/advertise"><Megaphone className="w-4 h-4 mr-1.5" /> Renew or upgrade</Link></Button>
          <Button variant="ghost" onClick={logout} data-testid="partner-logout"><LogOut className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-8" data-testid="partner-stats">
        <Stat icon={Eye} label="Impressions (30 days)" value={totals.impressions_30d} testId="partner-impressions-30d" />
        <Stat icon={MousePointerClick} label="Clicks (30 days)" value={totals.clicks_30d} testId="partner-clicks-30d" />
        <Stat icon={Eye} label="Impressions (all time)" value={totals.impressions} testId="partner-impressions-total" />
        <Stat icon={MousePointerClick} label="Clicks (all time)" value={totals.clicks} testId="partner-clicks-total" />
      </div>

      {last_30_days.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-8" data-testid="partner-daily">
          <p className="text-sm font-semibold mb-3">Daily activity</p>
          <div className="flex items-end gap-1 h-24">
            {last_30_days.map((d) => (
              <div key={d.date} title={`${d.date}: ${d.impressions} impressions, ${d.clicks} clicks`} className="flex-1 flex flex-col justify-end gap-0.5">
                <div className="bg-primary/70 rounded-t" style={{ height: `${Math.min(100, d.impressions * 8)}%` }} />
                <div className="bg-accent rounded-t" style={{ height: `${Math.min(100, d.clicks * 12)}%` }} />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Blue = impressions · Green = clicks</p>
        </div>
      )}

      <form onSubmit={save} className="rounded-2xl border border-border bg-card p-6 grid gap-4" data-testid="partner-listing-form">
        <h2 className="font-display font-bold">Your listing</h2>
        <div><label className="text-xs font-semibold text-muted-foreground">Company name</label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required minLength={2} maxLength={80} data-testid="partner-company-input" /></div>
        <div><label className="text-xs font-semibold text-muted-foreground">Description (shown to managers)</label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required minLength={10} maxLength={300} rows={3} data-testid="partner-description-input" /></div>
        <div><label className="text-xs font-semibold text-muted-foreground">Website</label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required data-testid="partner-url-input" /></div>
        <div><label className="text-xs font-semibold text-muted-foreground">Keywords that should trigger your placement (comma-separated)</label><Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="e.g. safeguarding training, first aid, moving and handling" data-testid="partner-keywords-input" /></div>
        <div><Button type="submit" disabled={saving} data-testid="partner-save"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save listing"}</Button></div>
      </form>
    </div>
  );
}
